/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { navigate } from '@reach/router';
import classnames from 'classnames';
import useEnhanced from 'effects/enhanced';
import Nav from '../Nav/Nav';
import Header from './Header/Header';
import Next from './Next/Next';
import renderContentBlock from './Content';
import useMenu from './menu';
import useAnimation from './animations';
import './Entry.scss';

/**
 * This is the Entry component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Entry({
  type,
  theme,
  excerpt,
  author,
  photographer,
  content,
  nav,
  labels,
  next,
  onTheme,
  ...props
}) {
  const enhanced = useEnhanced(660);
  const [ref, animate] = useAnimation();
  const menuRef = useMenu();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    onTheme(theme);
  }, [theme]);

  function renderExcerpt() {
    if (type === 'largeImage' && enhanced) return null;
    if (type === 'doubleTitle') return null;
    if (type === 'sideImage') return null;
    if (type === 'bottomImage') return null;
    if (type === 'fullHeightHalfImage') return null;

    return (
      <div className="excerpt" appear="true">
        {['largeImage', 'video'].indexOf(type) > -1 ? (
          <p
            dangerouslySetInnerHTML={{ __html: excerpt }}
            stagger-text="false"
          />
        ) : (
          <p dangerouslySetInnerHTML={{ __html: excerpt }} />
        )}
      </div>
    );
  }

  function renderCopyright() {
    if (type === 'doubleTitle') return null;
    if (type === 'largeIntro') return null;
    if (type === 'videoEntry') return null;
    if (type === 'sideImage') return null;
    if (type === 'bottomImage') return null;
    if (type === 'fullHeightHalfImage') return null;
    if (type === 'largeImage' && enhanced) return null;

    function renderContent() {
      if (photographer) {
        return (
          <>
            <span className="author">{author}</span>
            {' // '}
            <span className="photo">{photographer}</span>
          </>
        );
      }
      return <span className="author">{author}</span>;
    }

    return (
      <div key="copyright" className="copyright" stagger-text="true">
        {renderContent()}
      </div>
    );
  }

  function renderNext() {
    if (!next) return null;
    return (
      <Next
        {...next}
        onNavigate={url => {
          animate()
            .then(() => {
              navigate(url);
            })
            .catch(() => {
              navigate(url);
            });
        }}
      />
    );
  }

  return (
    <article ref={ref} className={classnames('page entry', type)}>
      <span className="span" />
      <Nav ref={menuRef} items={nav} labels={labels} />
      <Header
        enhanced={enhanced}
        type={type}
        excerpt={excerpt}
        author={author}
        photographer={photographer}
        {...props}
      />
      <main>
        {renderExcerpt()}
        {renderCopyright()}
        <div className="content-blocks">{content.map(renderContentBlock)}</div>
        {renderNext()}
      </main>
    </article>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Entry.propTypes = {
  type: PropTypes.oneOf([
    'doubleTitle',
    'largeImage',
    'largeIntro',
    'videoEntry',
    'sideImage',
    'bottomImage',
    'fullHeightHalfImage',
  ]).isRequired,
  theme: PropTypes.string,
  excerpt: PropTypes.string,
  author: PropTypes.string,
  photographer: PropTypes.string,
  content: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  next: PropTypes.object,
  labels: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  nav: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ).isRequired,
  onTheme: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Entry.defaultProps = {
  theme: 'white',
  type: 'doubleTitle',
  content: [],
  next: null,
  nav: [],
  labels: {
    name: 'Name',
    menu: 'Menu',
    open: 'Open',
    close: 'Close',
  },
  onTheme: () => {},
};

export default Entry;
